import React, { useEffect, useRef } from 'react';
import { useTaxForm } from './TaxFormContext';
import { MobileStepProgress } from './MobileStepProgress';
import { MobileNavigationButtons } from './MobileNavigationButtons';
import { Step1AccountInfo } from './steps/Step1AccountInfo';
import { Step2PersonalInfo } from './steps/Step2PersonalInfo';
import { Step3IncomeDetails } from './steps/Step3IncomeDetails';
import { Step4BankDetails } from './steps/Step4BankDetails';
import { Step5AssetsLiabilities } from './steps/Step5AssetsLiabilities';
import { Step6Review } from './steps/Step6Review';
import Loader from '@/components/Loader';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const MobileTaxFormContent: React.FC<{ isModal?: boolean }> = ({ isModal = false }) => {
  const { currentStep, isLoading } = useTaxForm();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isModal) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } else if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [currentStep, isModal]);

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1AccountInfo />;
      case 2: return <Step2PersonalInfo />;
      case 3: return <Step3IncomeDetails />;
      case 4: return <Step4BankDetails />;
      case 5: return <Step5AssetsLiabilities />;
      case 6: return <Step6Review />;
      default: return <Step1AccountInfo />;
    }
  };

  if (isModal) {
    return (
      <div className="flex flex-col h-full w-full bg-gradient-to-br from-indigo-50/50 via-white to-blue-50/50">
        <MobileStepProgress />
        <div className="flex-grow overflow-y-auto pb-32 w-full" ref={contentRef}>
          <div className="px-4 pb-4 w-full">
            <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] p-5 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] border border-white">
              <div className="animate-fade-in w-full">
                {isLoading ? <div className="py-12"><Loader /></div> : renderStep()}
              </div>
            </div>
          </div>
        </div>
        <MobileNavigationButtons />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh] bg-gradient-to-br from-slate-50 via-indigo-50/30 to-blue-50/40 relative overflow-hidden">
      
      {/* Decorative ambient background blur blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[30%] rounded-full bg-blue-400/10 blur-3xl" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-indigo-400/10 blur-3xl" />
      </div>

      <div className="relative z-10 flex flex-col min-h-[100dvh]">
        {/* Top Navigation Bar */}
        <div className="flex items-center justify-between p-4 bg-transparent">
           <button 
             onClick={() => navigate('/')}
             className="p-2.5 rounded-full bg-white/50 backdrop-blur-md hover:bg-white/80 active:scale-95 transition-all shadow-sm border border-white"
           >
             <X className="h-5 w-5 text-slate-700" strokeWidth={2.5} />
           </button>
           <h1 className="text-sm font-bold text-slate-600 uppercase tracking-widest">Filing</h1>
           <div className="w-10" /> {/* Spacer to balance the X button */}
        </div>

        <MobileStepProgress />

        <main className="flex-grow px-4 pb-32" ref={contentRef}>
          <div className="max-w-md mx-auto">
            <div className="animate-slide-up-fade">
              {isLoading ? (
                <div className="flex justify-center py-20"><Loader /></div>
              ) : (
                <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] p-6 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] border border-white overflow-hidden relative">
                   {/* Inner subtle glow */}
                   <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50" />
                   
                   {renderStep()}
                </div>
              )}
            </div>
          </div>
        </main>

        <MobileNavigationButtons />
      </div>
    </div>
  );
};
